import { DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent, merge, throttleTime } from 'rxjs';
import Keycloak from 'keycloak-js';
import { ACTIVITY_BROADCAST_DI_TOKEN } from '@core/broadcast/broadcast';
import { BroadcastMessageType } from '@core/broadcast/broadcast.model';
import { LoggerService } from '@core/log/services/logger';
import { nowMs } from '@core/formatting';

/**
 * Finestra entro cui l'attività dell'utente viene annunciata una volta sola, per non bombardare di messaggi le
 * altre schede del browser.
 */
export const USER_ACTIVITY_THROTTLE_MS = 30_000;

/**
 * Secondi di validità che il token deve ancora avere perché il rinnovo venga saltato. La soglia è più larga di
 * quella usata sulle richieste HTTP, così una scheda del browser in secondo piano rinnova con un margine maggiore e
 * non all'ultimo istante. Resta però una soglia e non un rinnovo forzato: un token ancora valido non costa una
 * chiamata.
 */
export const SESSION_RENEW_MIN_VALIDITY_S = 60;

/**
 * Secondi entro cui la scadenza del refresh token fa passare al rinnovo forzato.
 *
 * La soglia sopra riguarda l'access token, ed è l'unica cosa che `updateToken` guarda per decidere. A morire
 * però è il refresh token, che scade dopo SSO Session Idle: se quel tempo è vicino alla vita dell'access token,
 * il refresh token muore prima che la soglia si accorga di qualcosa. Questo secondo controllo toglie il
 * meccanismo dalla dipendenza dal rapporto fra due impostazioni del realm.
 */
export const SESSION_REFRESH_TOKEN_MIN_VALIDITY_S = 45;

/** Valore con cui `keycloak-js` salta ogni soglia e rinnova comunque. */
const FORCED_RENEW = -1;

const MS_PER_SECOND = 1000;

/** Nome del blocco che ogni scheda del browser detiene per restare sveglia. */
export const SESSION_KEEP_AWAKE_LOCK = 'session-keep-awake';

export interface SessionKeepAliveOptions {
  /** Nome del blocco, che ogni tentativo cambia per non condividerlo con le altre schede. */
  lockName?: string;
}

/**
 * Detiene un blocco in modalità condivisa finché il chiamante vive: possederne uno è fra le condizioni che
 * tengono una scheda del browser nascosta fuori dall'ibernazione, e una scheda ibernata non rinnova più il
 * proprio token.
 *
 * La modalità condivisa è obbligatoria: con un blocco esclusivo e un nome solo lo deterrebbe una scheda sola, e
 * resterebbero ibernabili le altre.
 */
const holdKeepAwakeLock = (lockName: string, destroyRef: DestroyRef, logger: LoggerService): void => {
  if (!('locks' in navigator)) {
    logger.warn('Web Locks API non disponibile: la scheda in secondo piano può essere ibernata');
    return;
  }
  const untilDestroyed = new Promise<void>((resolve) => {
    destroyRef.onDestroy(() => {
      resolve();
    });
  });
  navigator.locks
    .request(lockName, { mode: 'shared' }, () => untilDestroyed)
    .catch((error: unknown) => {
      logger.warn(`Blocco scheda non ottenuto: ${String(error)}`);
    });
};

/**
 * Tiene viva la sessione di questa scheda del browser e di eventuali altre schede finché l'utente lavora.
 *
 * Ogni scheda del browser conserva il proprio refresh token in memoria e nessuna lo può rinnovare per un'altra:
 * può solo annunciare che c'è attività. Durante l'attività, qui o altrove, la scheda guarda la scadenza del proprio
 * token e rinnova solo se serve, quindi questo canale si aggiunge al rinnovo innescato dalle richieste HTTP.
 */
export const injectSessionKeepAlive = (options?: SessionKeepAliveOptions): void => {
  const activityCh = inject(ACTIVITY_BROADCAST_DI_TOKEN);
  const keycloak = inject(Keycloak);
  const logger = inject(LoggerService);
  const destroyRef = inject(DestroyRef);

  holdKeepAwakeLock(options?.lockName ?? SESSION_KEEP_AWAKE_LOCK, destroyRef, logger);

  /** Vero quando il refresh token scade prima di quanto la soglia sull'access token riesca a vedere. */
  const refreshTokenIsExpiringSoon = (): boolean => {
    const expiresAtSeconds = keycloak.refreshTokenParsed?.exp;
    if (expiresAtSeconds === undefined) {
      return false;
    }
    const nowSeconds = Math.ceil(nowMs() / MS_PER_SECOND) - (keycloak.timeSkew ?? 0);
    return expiresAtSeconds - nowSeconds < SESSION_REFRESH_TOKEN_MIN_VALIDITY_S;
  };

  const renewIfNeeded = (): void => {
    // Senza token non c'è nulla da rinnovare: il rientro tocca a chi gestisce gli eventi di Keycloak.
    if (!keycloak.authenticated) {
      return;
    }
    const minValidity = refreshTokenIsExpiringSoon() ? FORCED_RENEW : SESSION_RENEW_MIN_VALIDITY_S;
    keycloak.updateToken(minValidity).catch((error: unknown) => {
      logger.warn(`Rinnovo del token non riuscito dopo l'annuncio di attività: ${String(error)}`);
    });
  };

  const othersActivity = activityCh.messagesOfType(BroadcastMessageType.USER_ACTIVITY).pipe(takeUntilDestroyed());
  othersActivity.subscribe(() => {
    renewIfNeeded();
  });

  /** Eventi che decretano l'uso attivo del sistema */
  const activityEvents = merge(
    fromEvent(document, 'pointerdown'),
    fromEvent(document, 'keydown'),
    fromEvent(document, 'wheel'),
    fromEvent(document, 'touchstart')
  );
  const userActivity = activityEvents.pipe(throttleTime(USER_ACTIVITY_THROTTLE_MS), takeUntilDestroyed());
  userActivity.subscribe(() => {
    /** Esegue il rinnovo della scheda corrente, poi invia l'evento alle altre schede */
    renewIfNeeded();
    activityCh.publish({ type: BroadcastMessageType.USER_ACTIVITY, payload: nowMs() });
  });
};
